import {
  THEME_STYLE_LISTS,
  themeStyleById,
  themeStylesOf,
} from '../../shared/theme/themeStyles';
import { readThemeConfiguration, withThemeConfiguration } from '../../shared/theme/themeConfiguration';
import { DOCUMENT_STYLE, DOCUMENT_STYLE_KINDS } from '../../shared/theme/styleMapping';
import { addStyle } from '../../shared/theme/styleList';
import { linkThemeStyle, mapThemedBlocksDeep } from '../puck/themedBlocks';

const BOX_BLOCK_PREFIX = 'box-';

/**
 * Ajoute au thème du site un style du document : même forme (format du
 * configurateur), identifiant neuf, comme une création dans le configurateur.
 */
function importStyle(configuration, kind, style) {
  const { patch, style: imported } = addStyle(configuration, THEME_STYLE_LISTS[kind], style);
  return { configuration: { ...configuration, ...patch }, imported };
}

/**
 * Rattache les tableaux et cartes info générés aux styles du thème du site,
 * selon la correspondance choisie (cf. shared/theme/styleMapping).
 *
 * Les styles du document à importer (le thème n'en avait aucun pour ce
 * composant, ou l'utilisateur a choisi de garder celui du document) sont
 * d'abord ajoutés au thème du site. Le thème du site et la
 * correspondance finale sont écrits dans `projectConfig` (`theme`,
 * `styleMapping`, relue à la régénération).
 *
 * @param {object} webData web_data sorti du parser
 * @param {{ used, plan, mapping?, theme, remember? }} params
 *        `theme` : celui du site (template, ou configuration du document si le
 *        template n'en a pas) ; `mapping` : réponses de l'utilisateur, par-dessus
 *        les propositions du plan ; `remember` : faux quand le thème est la
 *        configuration du document — ses identifiants ne sont pas des choix de
 *        styles du site, et ne doivent pas être relus comme tels si le template
 *        est configuré ensuite.
 */
export function applyThemeStyleMapping(webData, {
  used, plan, mapping = {}, theme, remember = true,
}) {
  let configuration = readThemeConfiguration(theme);
  // Pour lier les blocs : l'identifiant du style du site. Pour mémoire : la
  // réponse donnée — « style du document » reste tel quel, pour être redonné.
  const finalMapping = {};
  const rememberedMapping = {};

  DOCUMENT_STYLE_KINDS.forEach((kind) => {
    const answers = { ...plan[kind].mapping, ...(mapping[kind] || {}) };
    const resolved = { ...answers };
    const toImport = [
      ...plan[kind].imports,
      ...Object.keys(answers).filter((key) => answers[key] === DOCUMENT_STYLE),
    ];
    toImport.forEach((key) => {
      const entry = used.entries[kind].find((candidate) => candidate.key === key);
      if (!entry?.style) {
        resolved[key] = null;
        return;
      }
      const result = importStyle(configuration, kind, entry.style);
      configuration = result.configuration;
      resolved[key] = result.imported.id;
    });
    finalMapping[kind] = resolved;
    rememberedMapping[kind] = Object.fromEntries(Object.keys(resolved).map((key) => [
      key, answers[key] === DOCUMENT_STYLE ? DOCUMENT_STYLE : resolved[key],
    ]));
  });

  const linked = mapThemedBlocksDeep(webData, (kind, block) => {
    const { id } = block.props;
    if (typeof id !== 'string' || !id.startsWith(BOX_BLOCK_PREFIX)) return block;
    const source = used.boxKeys[id.slice(BOX_BLOCK_PREFIX.length)];
    if (!source || source.kind !== kind) return block;
    const style = themeStyleById(themeStylesOf(configuration, kind), finalMapping[kind][source.key]);
    // Sans style (carte gardée telle quelle) : `null` explicite, le bloc
    // garde l'aspect du document.
    const props = style
      ? linkThemeStyle(kind, block.props, style, configuration)
      : { ...block.props, themeStyleId: null };
    return { ...block, props };
  });

  return {
    ...linked,
    projectConfig: {
      ...linked.projectConfig,
      theme: withThemeConfiguration(linked.projectConfig?.theme, configuration),
      styleMapping: remember ? rememberedMapping : null,
    },
  };
}
